import {
  Backdrop,
  CircularProgress,
  Grid,
  Paper,
  Typography,
  Box,
} from "@mui/material";
import { useParams } from "react-router-dom";
// eslint-disable-next-line import/named
import { styled, useTheme } from "@mui/material/styles";
import React, { useEffect, useState } from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { SimpleTreeView } from "@mui/x-tree-view/SimpleTreeView";
import { TreeItem } from "@mui/x-tree-view/TreeItem";
import FolderIcon from "@mui/icons-material/Folder";
import {
  IWikiProduct,
  IWikiPage,
  getPublicListing,
  getWikiContent,
  IWikiFileData,
} from "../../api/wikiApi";
import { getProduct } from "../../api/Admin/dbProductsApi";
import WikiRenderer from "./WikiRenderer";
import { useSelector } from "react-redux";
import { ILoggedUser } from "../../types/Auth";
import { ToastContainer, toast } from "react-toastify";
import { RootState } from "../../redux/reducers/rootReducer";
import { docsResources } from "./.resources";
import { useResources } from "../../hooks/useResources";

const Manuals: React.FC = () => {
  const { productId } = useParams();
  const theme = useTheme();

  const [selectedItem, setSelectedItem] = useState<IWikiPage>();
  const [products, setProducts] = useState<IWikiProduct[]>([]);
  const [productName, setProductName] = useState<string>();
  const [content, setContent] = useState<IWikiFileData[]>([]);
  const [selectedContent, setSelectedContent] = useState<IWikiFileData>();
  const [isLoadingPage, setIsLoadingPage] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [expandedItems, setExpandedItems] = useState<string[]>([]);

  // Language sync is handled globally in App.tsx
  const res = useResources(docsResources);
  
  const language: string = useSelector((state: RootState) => {
    return state.language;
  });

  const loggedUser: ILoggedUser = useSelector((state: RootState) => {
    return state.userInfo;
  });


  const showError = (err: string | undefined) => {
    if (err) {
      toast.error(err, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    }
  };

  const getWiki = async () => {
    setIsLoading(true);
    let scopes: string[] = ["Public"];
    if (loggedUser.isAdmin) scopes = [...scopes, "Admin"];
    else if (loggedUser.isDistributor) scopes = [...scopes, "Distributor"];
    else if (loggedUser.isSupport) scopes = [...scopes, "Support"];
    else if (loggedUser.isCustomer) scopes = [...scopes, "Customer"];

    await getPublicListing(
      [productId],
      scopes,
      language,
      "Manuals",
      (wikis) => {
        setSelectedItem(undefined);
        // Filter valid wikis and deduplicate by productId
        const validWikis = wikis
          .filter((wiki) => wiki.productId && wiki.root)
          .filter(
            (wiki, index, self) =>
              index === self.findIndex((w) => w.productId === wiki.productId)
          );
        setProducts([...validWikis]);
        // Auto-expand first product for better UX
        if (validWikis.length > 0) {
          setExpandedItems([`manual-0-${validWikis[0].productId}`]);
        }
        setIsLoading(false);
        getProduct(
          productId,
          (prod) => {
            setProductName(prod.productName);
          },
          () => setProductName("")
        );
      },
      (error) => {
        showError(error);
        setIsLoading(false);
      }
    );
  };

  useEffect(() => {
    if (loggedUser?.userId && productId) {
      getWiki();
    }
  }, [productId]);

  useEffect(() => {
    if (loggedUser && productId) {
      getWiki();
    }
  }, [loggedUser]);

  /* Temporarily disable makeStyles - const useTreeItemStyles = makeStyles((theme) => ({
    root: {
      color: theme.palette.text.secondary,
      "&:hover > $content": {
        backgroundColor: theme.palette.action.hover,
      },
      "&:focus > $content, &$selected > $content": {
        backgroundColor: `var(--tree-view-bg-color, ${theme.palette.grey[400]})`,
        color: "var(--tree-view-color)",
      },
      "&:focus > $content $label, &:hover > $content $label, &$selected > $content $label": {
        backgroundColor: "transparent",
      },
    },
    node: {
      padding: "8px",
    },
    content: {
      color: theme.palette.text.secondary,
      borderTopRightRadius: theme.spacing(2),
      borderBottomRightRadius: theme.spacing(2),
      paddingRight: theme.spacing(1),
      fontWeight: theme.typography.fontWeightMedium,
      "$expanded > &": {
        fontWeight: theme.typography.fontWeightRegular,
      },
    },
    group: {
      marginLeft: 0,
      "& $content": {
        paddingLeft: theme.spacing(2),
      },
    },
    expanded: {},
    selected: {},
    label: {
      fontWeight: "inherit",
      color: "inherit",
    },
    labelRoot: {
      display: "flex",
      alignItems: "center",
      padding: theme.spacing(0.5, 0),
    },
    labelIcon: {
      marginRight: theme.spacing(1),
    },
    labelText: {
      fontWeight: "inherit",
      flexGrow: 1,
    },
  })); */

  const findPage = (page: IWikiPage, id: string) => {
    if (page.id === id) {
      return page;
    }
    if (page.pages) {
      let match: IWikiPage | undefined;
      page.pages.forEach((subPage) => {
        if (!match) {
          match = findPage(subPage, id);
          if (match) {
            return;
          }
        }
      });
      return match;
    }
    return undefined;
  };

  const handleTreeViewSelectItem = (
    event: React.SyntheticEvent | null,
    itemId: string,
    isSelected: boolean
  ) => {
    if (!itemId || !isSelected) return;

    // Skip if this is a manual root node (not a page)
    if (itemId.startsWith("manual-") && !itemId.includes("-page-")) return;

    // Extract the original page ID from the unique item ID
    const actualPageId = itemId.includes("-page-")
      ? itemId.split("-page-").pop()
      : itemId;
    if (!actualPageId) return;

    setSelectedItem(undefined);
    setSelectedContent(undefined);
    setIsLoadingPage(false);

    products.forEach((prod) => {
      const match = findPage(prod.root, actualPageId);
      if (match) {
        setSelectedItem(match);
        const selectedContent = content?.find((c) => c.id === actualPageId);
        if (!selectedContent && match.hasContent) {
          setIsLoadingPage(true);
          getWikiContent(
            match.id,
            (c) => {
              const file = { ...c, id: actualPageId };
              setContent([...content, file]);
              setSelectedContent(file);
              setIsLoadingPage(false);
            },
            (notFound) => {
              const file = {
                ...notFound,
                content: res.notFoundMessage,
                id: actualPageId,
              };
              setContent([...content, file]);
              setSelectedContent(file);
              setIsLoadingPage(false);
            },
            () => {
              setIsLoadingPage(false);
            }
          );
        } else {
          setSelectedContent(selectedContent);
        }
        return;
      }
    });
  };

  const handleTreeViewExpandedItemsChange = (
    event: React.SyntheticEvent | null,
    itemId: string,
    isExpanded: boolean
  ) => {
    if (isExpanded) {
      setExpandedItems((prev) => [...prev, itemId]);
    } else {
      setExpandedItems((prev) => prev.filter((id) => id !== itemId));
    }
  };

  // Recursive function to render TreeItems
  const renderTreeItems = (
    page: IWikiPage,
    prod: IWikiProduct,
    level = 0,
    parentId = ""
  ): React.ReactNode => {
    if (!page || !page.id) {
      return null;
    }

    if (!page.name) {
      page.name = productName || prod.productName || "Manual";
    }

    const hasChildren = page.pages && page.pages.length > 0;
    const iconSize = level === 0 ? "18px" : "16px";
    const textVariant = level === 0 ? "body1" : "body2";
    // Ensure unique ID by combining parent context with page ID
    const uniqueItemId = parentId
      ? `${parentId}-page-${page.id}`
      : `page-${page.id}`;

    return (
      <TreeItem
        key={uniqueItemId}
        itemId={uniqueItemId}
        label={
          <div
            style={{ display: "flex", alignItems: "center", padding: "2px 0" }}
          >
            <FolderIcon
              style={{
                marginRight: "8px",
                fontSize: iconSize,
                color: hasChildren ? theme.palette.primary.main : "#666",
              }}
            />
            <Typography
              variant={textVariant as "h6" | "body1" | "body2"}
              style={{ fontWeight: level === 0 ? 500 : 400 }}
            >
              {page.name}
            </Typography>
          </div>
        }
      >
        {hasChildren &&
          page.pages
            .map((childPage) =>
              renderTreeItems(childPage, prod, level + 1, uniqueItemId)
            )
            .filter(Boolean)}
      </TreeItem>
    );
  };

  const StyledHeaderContainer = styled(Grid)(() => ({
    padding: "0px 6px 0px 32px",
  }));

  const StyledRendererContainer = styled("div")(() => ({
    padding: "16px 64px 16px 64px",
  }));

  return (
    <div>
      <ToastContainer />
      <Backdrop
        sx={{ zIndex: (theme) => theme.zIndex.drawer + 1, color: "#fff" }}
        open={isLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Grid container>
        <Grid size={{ xs: 12, sm: 2 }}>
          <>
            <StyledHeaderContainer container direction="column">
              <>
                <Typography variant="h3">{productName}</Typography>
                <Typography variant="h6">{res.manuals}</Typography>
              </>
            </StyledHeaderContainer>

            <Box sx={{ p: 2 }}>
              {products.length > 0 ? (
                <SimpleTreeView
                  onItemSelectionToggle={handleTreeViewSelectItem}
                  onItemExpansionToggle={handleTreeViewExpandedItemsChange}
                  expandedItems={expandedItems}
                  slots={{
                    expandIcon: ChevronRightIcon,
                    collapseIcon: ExpandMoreIcon,
                  }}
                  sx={{
                    flexGrow: 1,
                    maxWidth: 400,
                    // Target all possible TreeItem class names
                    '& [role="treeitem"]': {
                      '& [role="group"]': {
                        paddingLeft: "12px !important",
                        marginLeft: "8px",
                        borderLeft: "1px dashed rgba(0, 0, 0, 0.3)",
                        '& [role="group"]': {
                          paddingLeft: "12px !important",
                          borderLeft: "1px dashed rgba(0, 0, 0, 0.2)",
                        },
                      },
                    },
                    // Also try standard MUI classes
                    "& .MuiTreeItem-content, & .MuiTreeItem2-content": {
                      padding: "4px 8px",
                    },
                    "& .MuiTreeItem-group, & .MuiTreeItem2-group": {
                      marginLeft: "8px !important",
                      paddingLeft: "12px !important",
                      borderLeft: "1px dashed rgba(0, 0, 0, 0.3)",
                    },
                  }}
                >
                  {products
                    .filter((product) => product.productId && product.root)
                    .map((product, index) =>
                      renderTreeItems(
                        product.root,
                        product,
                        0,
                        `manual-${index}-${product.productId}`
                      )
                    )}
                </SimpleTreeView>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {isLoading
                    ? "Loading manual content..."
                    : "No manual content available"}
                </Typography>
              )}
            </Box>
          </>
        </Grid>

        <Grid size={{ xs: 12, md: 10 }}>
          <Paper>
            <StyledRendererContainer>
              {selectedItem && (
                <WikiRenderer
                  title={undefined}
                  url={selectedItem.url}
                  isLoading={isLoadingPage}
                  date={selectedContent?.date}
                  data={selectedContent?.content}
                />
              )}
            </StyledRendererContainer>
          </Paper>
        </Grid>
      </Grid>
    </div>
  );
};

export default Manuals;
